export interface GovModule {
  id: string
  name: string
  promise: string
  detail: string
  points: string[]
}

/** The three modules of the platform, in the order a ministry meets them. */
export const GOV_MODULES: GovModule[] = [
  {
    id: 'delivery-tracker',
    name: 'Delivery Tracker',
    promise: 'One live dashboard across every agency.',
    detail:
      'Each agency updates its own programmes against the plan’s own structure, through a simple form or a bulk upload. The ministry gets one live dashboard across every agency, and can see at a glance what has not been reported.',
    points: [
      'Agency self service reporting, by form or bulk upload',
      'The plan’s own pillars, programmes and indicators, not a new framework',
      'A standing view of which agencies have not reported this quarter',
    ],
  },
  {
    id: 'development-finance',
    name: 'Development Finance Intelligence',
    promise: 'Partner funding, mapped to the plan it is asked to align with.',
    detail:
      'We map development partner commitments and disbursements against plan programmes, so a ministry can show donors exactly where their funding lands against the plan they are asked to align with.',
    points: [
      'Commitments and disbursements joined to plan programmes',
      'Financing gaps visible by pillar and by programme',
      'One shared record for partner coordination meetings',
    ],
  },
  {
    id: 'ai-reporting',
    name: 'AI Reporting',
    promise: 'The reports your team writes by hand, drafted from live figures.',
    detail:
      'The platform turns the data agencies enter into the reports they currently write by hand, quarterly monitoring, SDG and review submissions, and donor facing progress. Every report is drafted from live figures, labelled as AI assisted, and approved by a named official before it leaves the platform.',
    points: [
      'Quarterly monitoring and review submissions',
      'SDG and donor facing progress reporting',
      'Every draft labelled as AI assisted and approved by a named official',
    ],
  },
]

export interface GovAudience {
  name: string
  need: string
}

export const GOV_AUDIENCES: GovAudience[] = [
  {
    name: 'Ministries of finance and planning',
    need: 'And their monitoring and evaluation offices, who answer for the plan as a whole.',
  },
  {
    name: 'Agencies that own delivery',
    need: 'The people who run the programmes and have to report on them.',
  },
  {
    name: 'Development partners',
    need: 'Who fund national plans and need to see results against them.',
  },
]

/** What a government controls, stated before the benefits. */
export const GOV_GUARANTEES: string[] = [
  'Government data stays under the government’s control, in a region the country accepts.',
  'A full export of your data is available at any time.',
  'AI drafted outputs are labelled as AI generated and reviewed by a named official.',
  'No automated decision is taken about an individual citizen.',
]

export const GOV_PILOT_STEPS: { step: string; detail: string }[] = [
  {
    step: 'One ministry, a small set of agencies',
    detail:
      'We start narrow, so the first quarterly cycle is completed rather than designed. This is usually financed as development partner technical assistance rather than a direct purchase.',
  },
  {
    step: 'Scale across agencies',
    detail:
      'Once one cycle has run end to end, the same structure extends to the remaining agencies without a new build.',
  },
  {
    step: 'Then across countries',
    detail:
      'Beneath the indicator layer the platform is generic, because the reporting duty is common to plans aligned to the SDGs, Agenda 2063 and ECOWAS Agenda 2050.',
  },
]
